/**
 * The online fight this browser is in, kept in localStorage so a reloaded or reopened page can
 * offer to rejoin it (D19): the server holds the seat for ONLINE.reconnectSeconds, with the fight
 * paused. Small and dependency-free, so Home can check it without loading the online code.
 */
import { ONLINE } from '@caged/shared';
import type { MatchInfo } from './online';

export interface ActiveFight {
  /** Colyseus reconnection token ("roomId:token"). */
  token: string;
  info: MatchInfo;
  /** Date.now() when a page last had this fight open. */
  aliveAt: number;
  /** Where the fight was, for the rejoin card. */
  round: number;
  /** Seconds left in the round. */
  clock: number;
}

const KEY = 'caged.activeFight.v1';

export function saveActiveFight(fight: ActiveFight): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(fight));
  } catch {
    // Storage full or blocked: there just won't be a rejoin offer.
  }
}

/** Keeps the saved fight current (called about once a second while it runs). */
export function touchActiveFight(update: Partial<Omit<ActiveFight, 'info'>>): void {
  const saved = loadActiveFight();
  if (saved) saveActiveFight({ ...saved, ...update, aliveAt: Date.now() });
}

export function clearActiveFight(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

export function loadActiveFight(): ActiveFight | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const f = JSON.parse(raw) as ActiveFight;
    return typeof f?.token === 'string' && f.info?.match ? f : null;
  } catch {
    return null;
  }
}

/** Seconds left to rejoin before the fight counts as a forfeit. */
export function rejoinSecondsLeft(fight: ActiveFight, now = Date.now()): number {
  return ONLINE.reconnectSeconds - (now - fight.aliveAt) / 1000;
}

/** The saved fight if it can still be rejoined; an expired one is cleared. */
export function rejoinableFight(): ActiveFight | null {
  const f = loadActiveFight();
  // A second of margin: the server's hold started when the page went away.
  if (f && rejoinSecondsLeft(f) > 1) return f;
  if (f) clearActiveFight();
  return null;
}
